import React, { FC } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { ScannerCaseAuthStatus } from 'models/scanner-case';
import { threeShapeClinicStatusTemplate } from 'apollo/templates/clinic';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { CONSTANTS } from 'config/constants';

// dcmaccess is what lets the token download the case's DCM files. Without it every DCM
// attachment answers 400 and the whole case ends in ERROR (Region Jönköping, 2026-10-05).
// Keep in step with THREE_SHAPE_CLIENT_SCOPE, which cron-service's automatic login uses.
export const THREE_SHAPE_SCOPE =
    'openid+api+offline_access+communicate.connections.manage+data.companies.read_only+data.users.read_only+dcmaccess';

type ThreeShapeClinicAuthProps = {
    clinicId: string;
};

const ThreeShapeClinicAuth: FC<ThreeShapeClinicAuthProps> = ({ clinicId }) => {
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data, loading } = useQuery<{ threeShapeClinicStatus: ScannerCaseAuthStatus }>(
        threeShapeClinicStatusTemplate,
        {
            variables: { clinicId },
            fetchPolicy: 'network-only',
            onError: showBoundary,
        },
    );

    const buildAuthUrl = (isDeleteAuth: boolean) => {
        const status = data?.threeShapeClinicStatus;
        if (!status) return '';
        const state = btoa(JSON.stringify({ clinicId, isDeleteAuth }));
        return (
            `${CONSTANTS.THREE_SHAPE_AUTH_URL}/connect/authorize` +
            `?client_id=${status.clientId}` +
            `&response_type=code` +
            `&scope=${THREE_SHAPE_SCOPE}` +
            `&redirect_uri=${CONSTANTS.THREE_SHAPE_AUTH_REDIRECT}` +
            `&code_challenge=${status.codeChallenge}` +
            `&code_challenge_method=S256` +
            `&response_mode=query` +
            `&prompt=login` +
            `&max_age=0` +
            `&state=${encodeURIComponent(state)}`
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (!data?.threeShapeClinicStatus?.enabled) {
        return null;
    }

    return (
        <Box p={2} display="flex" flexDirection="column" gap={1}>
            <Typography variant="h4">{translate('organization:THREE_SHAPE_ADMIN_AUTH')}</Typography>
            <Alert severity="info">{translate('organization:THREE_SHAPE_INCOGNITO_INSTRUCTIONS')}</Alert>
            <Box display="flex" gap={2}>
                <Button
                    variant="outlined"
                    size="small"
                    href={buildAuthUrl(false)}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {translate('organization:THREE_SHAPE_LOGIN')}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    href={buildAuthUrl(true)}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {translate('organization:THREE_SHAPE_DELETE_AUTH_LOGIN')}
                </Button>
            </Box>
        </Box>
    );
};

export default ThreeShapeClinicAuth;
